import { digObject, toggleArray } from '@domain-group/javascript-utils'

import useAgencyPaymentMethods from '@hooks/useAgencyPaymentMethods'
import useCampaignQuotes from '@hooks/useCampaignQuotes'
import useFinanceApplication, { useFinanceApplicationForm } from '@hooks/useFinanceApplication'
import useSelector from '@hooks/useSelector'

import type { CampaignModel, CampaignQuoteModel } from '@models/types'

type SelectQuoteParams = {
  campaignQuote: CampaignQuoteModel,
  campaignQuoteIds: number[],
  filteredCampaignQuotes: CampaignQuoteModel[],
  setEntityState: (arg: any) => void,
}

const selectQuote = (params: SelectQuoteParams) => {
  const {
    campaignQuote, campaignQuoteIds, filteredCampaignQuotes, setEntityState,
  } = params
  const updatedSelection = toggleArray(campaignQuoteIds, campaignQuote.id)

  const newTotal = updatedSelection.reduce((acc, quoteId) => {
    const quote = filteredCampaignQuotes.find(q => q.id === quoteId)
    if (quote){
      return acc + quote.total
    }

    return acc
  }, 0)

  setEntityState({
    data: { campaign_quote_ids: updatedSelection },
    total: newTotal,
  })
}

const useCreateFinanceApplicationModal = (campaign: CampaignModel) => {
  const initApplication = {
    owner_id: campaign.agency_id,
    owner_type: 'Agency',
    subject_id: campaign.id,
    subject_type: 'Campaign',
  }

  const financeApplicationForm = useFinanceApplicationForm(initApplication, { validateOn: 'change' })

  const {
    entityState: {
      data,
    },
    setEntityState,
  } = financeApplicationForm

  const campaignQuoteIds = digObject(data, 'campaign_quote_ids', [])

  const {
    callbacks: { createFinanceApplication: createFn },
    creating,
  } = useFinanceApplication()

  const campaignQuotesPayload = useCampaignQuotes({
    filters: { campaign_id: campaign.id },
    performHttpRequests: !!campaign.id,
  })
  const { filteredCampaignQuotes, hasFilteredCampaignQuotes, loading: loadingQuotes } = campaignQuotesPayload

  const agencyPaymentMethodsPayload = useAgencyPaymentMethods({ filters: { agency_id: campaign.agency_id } })
  const { filteredAgencyPaymentMethods } = agencyPaymentMethodsPayload

  const paymentMethods = useSelector(reduxState => reduxState.entities.paymentMethods)

  return {
    callbacks: {
      createFn,
      selectQuote: (campaignQuote: CampaignQuoteModel) => selectQuote({
        // @ts-expect-error TS2322
        campaignQuote, campaignQuoteIds, filteredCampaignQuotes, setEntityState,
      }),
    },
    campaignQuoteIds,
    creating,
    filteredAgencyPaymentMethods,
    filteredCampaignQuotes,
    financeApplicationForm,
    hasFilteredCampaignQuotes,
    loadingQuotes,
    paymentMethods,
  }
}

export type useCreateFinanceApplicationModalPayload = ReturnType<typeof useCreateFinanceApplicationModal>

export default useCreateFinanceApplicationModal
